import { MoreHoriz } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { dateFormat, dateFormatLongDate, pastDateTime } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';

import IntercomIcon from '../../../../../assets/img/backoffice/intercom.svg';
import PipedriveIcon from '../../../../../assets/img/backoffice/pipedrive.svg';
import StripeIcon from '../../../../../assets/img/backoffice/stripe.svg';
import { getCountryLabelByCountryCode } from '../../../../../constants/Countries';
import { OrganizationsTableColumn } from '../../../../../context/backoffice/tableColumns/organizations';
import { Organization } from '../../../../../domain/backoffice/organization/Organization';
import { StatusType } from '../../../../../domain/backoffice/subscription/Subscription';
import { User } from '../../../../../domain/user/User';
import Text from '../../../../../react-components/Text';
import { AlignmentTypes, DataTableColumnProps } from '../../../../../react-components/data-table/DataTable';
import {
  generateIntercomLink,
  generatePipedriveLink,
  generateStripeCustomerLink,
} from '../../../shared/SystemsInfoBox';
import { BackofficeClientsOrganizationActionsMenu } from '../shared/BackofficeClientsOrganizationActionsMenu';

export const getPaymentStatusColor = (status: StatusType) => {
  switch (status) {
    case `active`:
      return RoutalPalette.success.medium;
    case `trialing`:
      return RoutalPalette.warning.medium;
    default:
      return RoutalPalette.error.medium;
  }
};

type OrganizationsGetColumnsResponse = { [key: string]: any }[];

type OrganizationsGetColumns = {
  loggedUser?: User;
  adminUser?: User;
  tableColumns: Array<OrganizationsTableColumn>;
  onRefreshContent: () => void;
  goToOrganizationDetails: ({ id }: { id: string }) => void;
};

export const getOrganizationsColumns = ({
  loggedUser,
  adminUser,
  tableColumns,
  onRefreshContent,
  goToOrganizationDetails,
}: OrganizationsGetColumns): OrganizationsGetColumnsResponse => {
  const columns: Array<DataTableColumnProps> = [];

  tableColumns.forEach(({ displayable, identifier, sortable }: OrganizationsTableColumn) => {
    if (displayable) {
      switch (identifier) {
        case `name`:
          columns.push({
            width: 200,
            height: 45,
            sortable,
            accessorName: `name`,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.organizations.name" />
            ),
            formatter: ({ doc: organization }: { doc: Organization }) => {
              const name = organization.label;

              return (
                <Grid container direction="row" alignItems="center">
                  {name && name !== `` ? (
                    <RoutalTooltip title={name}>
                      <Text variant="button" onClick={(e: any) => goToOrganizationDetails({ id: organization.id })}>
                        {name.length > 35 ? `${name.substring(0, 35)}...` : `${name}`}
                      </Text>
                    </RoutalTooltip>
                  ) : (
                    <Text
                      variant="button"
                      onClick={(e: any) => goToOrganizationDetails({ id: organization.id })}
                      style={{
                        fontWeight: 500,
                        color: RoutalPalette.secondary00,
                      }}
                      intlId="no_name"
                    />
                  )}
                </Grid>
              );
            },
          });
          break;
        case `external_services`:
          columns.push({
            width: 120,
            columnStyle: {
              width: `unset`,
            },
            align: `center` as AlignmentTypes,
            sortable,
            formatter: ({ doc: organization }: { doc: Organization }) => {
              const owner = organization.users.find((user) => user.id === organization.owner_id);

              return (
                <Grid container direction="row">
                  <img
                    src={IntercomIcon}
                    style={{
                      width: `18px`,
                      cursor: owner?.email ? `pointer` : `default`,
                      opacity: owner?.email ? 1 : 0.5,
                    }}
                    alt=""
                    draggable="false"
                    onClick={() => {
                      if (owner?.email) {
                        window.open(generateIntercomLink(owner.email), `_blank`);
                      }
                    }}
                  />
                  <HSpacer small />
                  <img
                    src={StripeIcon}
                    style={{
                      width: `18px`,
                      cursor: organization.subscription.stripe_customer_id ? `pointer` : `default`,
                      opacity: organization.subscription.stripe_customer_id ? 1 : 0.5,
                    }}
                    alt=""
                    draggable="false"
                    onClick={() => {
                      if (organization.subscription.stripe_customer_id) {
                        window.open(generateStripeCustomerLink(organization.subscription.stripe_customer_id), `_blank`);
                      }
                    }}
                  />
                  <HSpacer small />
                  <img
                    src={PipedriveIcon}
                    style={{
                      width: `18px`,
                      cursor: organization.subscription.pipedrive_person_id ? `pointer` : `default`,
                      opacity: organization.subscription.pipedrive_person_id ? 1 : 0.5,
                    }}
                    alt=""
                    draggable="false"
                    onClick={() => {
                      if (organization.subscription.pipedrive_person_id) {
                        window.open(generatePipedriveLink(organization.subscription.pipedrive_person_id), `_blank`);
                      }
                    }}
                  />
                </Grid>
              );
            },
          });
          break;
        case `country`:
          columns.push({
            width: 100,
            sortable,
            accessorName: `country`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.organizations.country"
              />
            ),
            formatter: ({ doc: organization }: { doc: Organization }) => {
              if (!organization.country_code) return undefined;
              return (
                <Text>
                  {getCountryLabelByCountryCode({
                    countryCode: organization.country_code.toUpperCase(),
                    userLanguage: adminUser?.language ?? loggedUser?.language,
                  }) ?? `-`}
                </Text>
              );
            },
          });
          break;
        case `subscription_plan`:
          columns.push({
            width: 100,
            sortable,
            accessorName: `subscription_plan`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.organizations.subscription_plan"
              />
            ),
            formatter: ({ doc: organization }: { doc: Organization }) => {
              return <Text style={{ textTransform: `capitalize` }}>{organization.subscription.plan}</Text>;
            },
          });
          break;
        case `contract_enabled`:
          columns.push({
            width: 100,
            sortable,
            accessorName: `contract_enabled`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.organizations.contract_enabled"
              />
            ),
            formatter: ({ doc: organization }: { doc: Organization }) => {
              return <Text intlId={organization.subscription.has_contract ? `enabled` : `disabled`} />;
            },
          });
          break;
        case `subscription_licenses`:
          columns.push({
            width: 100,
            sortable,
            accessorName: `subscription_licenses`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.organizations.subscription_licenses"
              />
            ),
            formatter: ({ doc: organization }: { doc: Organization }) => {
              return <Text>{organization.subscription.vehicles}</Text>;
            },
          });
          break;
        case `users`:
          columns.push({
            width: 100,
            sortable,
            accessorName: `users`,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.organizations.users" />
            ),
            formatter: ({ doc: organization }: { doc: Organization }) => {
              return <Text>{organization.users.length}</Text>;
            },
          });
          break;
        case `projects`:
          columns.push({
            width: 100,
            sortable,
            accessorName: `projects`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.organizations.projects"
              />
            ),
            formatter: ({ doc: organization }: { doc: Organization }) => {
              return <Text>{organization.projects.length}</Text>;
            },
          });
          break;
        case `emails_usage`:
          columns.push({
            width: 100,
            sortable,
            accessorName: `emails_usage`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.organizations.emails_usage"
              />
            ),
            formatter: ({ doc: organization }: { doc: Organization }) => {
              return (
                <Text>{`${
                  organization.subscription.emails.period_usage
                } / ${organization.subscription.getSubscriptionMaxCustomerEmails()}`}</Text>
              );
            },
          });
          break;
        case `whatsapp_enabled`:
          columns.push({
            width: 100,
            sortable,
            accessorName: `whatsapp_enabled`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.organizations.whatsapp_enabled"
              />
            ),
            formatter: ({ doc: organization }: { doc: Organization }) => {
              const isEnabled = !!organization.subscription.whatsapp.enabled;
              return (
                <Text
                  intlId={isEnabled ? `enabled` : `disabled`}
                  style={{ color: isEnabled ? RoutalPalette.success.medium : RoutalPalette.error.medium }}
                />
              );
            },
          });
          break;
        case `remaining_whatsapp`:
          columns.push({
            width: 100,
            sortable,
            accessorName: `remaining_whatsapp`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.organizations.remaining_whatsapp"
              />
            ),
            formatter: ({ doc: organization }: { doc: Organization }) => {
              console.log(organization.subscription.whatsapp.credit);
              return <Text>{organization.subscription.whatsapp.credit ?? 0}</Text>;
            },
          });
          break;
        case `sms_enabled`:
          columns.push({
            width: 100,
            sortable,
            accessorName: `sms_enabled`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.organizations.sms_enabled"
              />
            ),
            formatter: ({ doc: organization }: { doc: Organization }) => {
              const isEnabled = !!organization.subscription.sms.enabled;
              return (
                <Text
                  intlId={isEnabled ? `enabled` : `disabled`}
                  style={{ color: isEnabled ? RoutalPalette.success.medium : RoutalPalette.error.medium }}
                />
              );
            },
          });
          break;
        case `remaining_sms`:
          columns.push({
            width: 100,
            sortable,
            accessorName: `remaining_sms`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.organizations.remaining_sms"
              />
            ),
            formatter: ({ doc: organization }: { doc: Organization }) => {
              return <Text>{organization.subscription.sms.credit ?? 0}</Text>;
            },
          });
          break;
        case `geocode_credit`:
          columns.push({
            width: 100,
            sortable,
            accessorName: `geocode_credit`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.organizations.geocode_credit"
              />
            ),
            formatter: ({ doc: organization }: { doc: Organization }) => {
              return <Text>{organization.subscription.geocode_credit}</Text>;
            },
          });
          break;
        case `payment_status`:
          columns.push({
            width: 100,
            sortable,
            accessorName: `payment_status`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.organizations.payment_status"
              />
            ),
            formatter: ({ doc: organization }: { doc: Organization }) => (
              <Text
                style={{ color: getPaymentStatusColor(organization.subscription.status), textTransform: `uppercase` }}
              >
                {organization.subscription.status}
              </Text>
            ),
          });
          break;
        case `payment_date`:
          columns.push({
            width: 100,
            sortable,
            accessorName: `payment_date`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.organizations.payment_date"
              />
            ),
            formatter: ({ doc: organization }: { doc: Organization }) => {
              const currentPeriodEnd = organization.subscription.current_period_end;
              return <Text variant="body">{dateFormatLongDate(currentPeriodEnd)}</Text>;
            },
          });
          break;
        default:
          break;
      }
    }
  });

  return [
    ...columns,
    ...(tableColumns.find(({ displayable, identifier }) => identifier === `created_at` && displayable)
      ? [
          {
            width: 60,
            columnStyle: {
              width: `unset`,
            },
            accessorName: `created_at`,
            align: `center` as AlignmentTypes,
            sortable: true,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.organizations.created_at"
              />
            ),
            formatter: ({ value: createdAt }) => (
              <RoutalTooltip title={dateFormat(createdAt)}>
                <Text variant="body">{pastDateTime(createdAt)}</Text>
              </RoutalTooltip>
            ),
          } as DataTableColumnProps,
        ]
      : []),
    {
      column: `actions`,
      name: (
        <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.organizations.actions" />
      ),
      align: `center`,
      width: 120,
      columnStyle: {
        width: `120px`,
      },
      valueCellStyle: {
        width: `100%`,
      },
      formatter: ({ doc: organization }: { doc: Organization }) => (
        <Grid container direction="row" wrap="nowrap" justifyContent="center">
          <BackofficeClientsOrganizationActionsMenu
            organization={organization}
            type="popover"
            popoverProps={{
              hideArrow: true,
              content: <MoreHoriz />,
              containerStyle: {
                width: `unset`,
              },
              style: { height: `18px`, color: RoutalPalette.secondary60, padding: 0, margin: 0, border: `unset` },
            }}
            onManageSMS={() => onRefreshContent()}
            onManageWhatsApp={() => onRefreshContent()}
            onManageGeocodeCredit={() => onRefreshContent()}
            onManageContractUpdate={() => onRefreshContent()}
          />
        </Grid>
      ),
    },
  ] as Array<DataTableColumnProps>;
};
